import React from 'react';
import { Empty, Space, Card, Typography, Button, Tag } from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    DragOutlined,
    CheckCircleOutlined,
} from '@ant-design/icons';
import { FormField_V2 } from '../../../common/typescript/models/CollectFormV2';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const { Text } = Typography;

type FormCanvasProps = {
    fields: FormField_V2[];
    onFieldEdit: (field: FormField_V2) => void;
    onFieldDelete: (fieldId: string) => void;
    onFieldReorder?: (fromIndex: number, toIndex: number) => void;
};

type SortableFieldItemProps = {
    field: FormField_V2;
    onEdit: () => void;
    onDelete: () => void;
};

const SortableFieldItem: React.FC<SortableFieldItemProps> = ({ field, onEdit, onDelete }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: field.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div ref={setNodeRef} style={style}>
            <Card
                size="small"
                hoverable
                style={{
                    borderLeft: field.required ? '3px solid #ff4d4f' : '3px solid #1890ff',
                    cursor: isDragging ? 'grabbing' : 'default',
                }}
                bodyStyle={{ padding: '12px' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div
                        {...attributes}
                        {...listeners}
                        style={{
                            cursor: 'grab',
                            display: 'flex',
                            alignItems: 'center',
                            touchAction: 'none',
                        }}
                    >
                        <DragOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                    </div>

                    <div style={{ flex: 1 }}>
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                            <Space>
                                <Text strong style={{ fontSize: '13px' }}>{field.label}</Text>
                                {field.required && (
                                    <Tag color="red" style={{ margin: 0, fontSize: '11px' }}>
                                        Bắt buộc
                                    </Tag>
                                )}
                            </Space>
                            <Space size={8}>
                                <Text type="secondary" style={{ fontSize: '11px' }}>
                                    ID: {field.id}
                                </Text>
                                <Text type="secondary" style={{ fontSize: '11px' }}>
                                    •
                                </Text>
                                <Text type="secondary" style={{ fontSize: '11px' }}>
                                    Loại: {field.type}
                                </Text>
                            </Space>
                            {field.placeholder && (
                                <Text type="secondary" italic style={{ fontSize: '11px' }}>
                                    Gợi ý: {field.placeholder}
                                </Text>
                            )}
                        </Space>
                    </div>

                    <Space size={4}>
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={onEdit}
                        />
                        <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={onDelete}
                        />
                    </Space>
                </div>
            </Card>
        </div>
    );
};

export const FormCanvas: React.FC<FormCanvasProps> = ({
    fields,
    onFieldEdit,
    onFieldDelete,
    onFieldReorder,
}) => {
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Chỉ kích hoạt drag sau khi di chuyển 8px
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = fields.findIndex((f) => f.id === active.id);
            const newIndex = fields.findIndex((f) => f.id === over.id);

            if (onFieldReorder) {
                onFieldReorder(oldIndex, newIndex);
            }
        }
    };

    if (fields.length === 0) {
        return (
            <div
                style={{
                    height: '200px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#fafafa',
                    border: '2px dashed #d9d9d9',
                    borderRadius: '8px',
                }}
            >
                <Empty
                    description={
                        <Space direction="vertical" align="center">
                            <Text type="secondary">Chưa có trường dữ liệu nào</Text>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Nhấn nút "Thêm trường" ở trên để bắt đầu tạo biểu mẫu
                            </Text>
                        </Space>
                    }
                />
            </div>
        );
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
            <SortableContext
                items={fields.map((f) => f.id)}
                strategy={verticalListSortingStrategy}
            >
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                    {fields.map((field) => (
                        <SortableFieldItem
                            key={field.id}
                            field={field}
                            onEdit={() => onFieldEdit(field)}
                            onDelete={() => onFieldDelete(field.id)}
                        />
                    ))}
                </Space>
            </SortableContext>
        </DndContext>
    );
};
